import { forwardRef, memo, useEffect, useRef } from 'react';
import type { ComponentPropsWithoutRef, ReactElement } from 'react';

import { cx } from '@/utils/cx';
import { pickDataAttrs } from '@/utils/pick-data-attrs';

import { useActionContext, useScrollContext } from '../HeaderContext';

import styles from './HeaderAction.module.scss';

import type { VisibleOn } from '../types';

export type ActionVariant = 'icon' | 'label';
type WrapperDataAttributes = Record<`data-${string}`, string | number | boolean | undefined>;

export interface HeaderActionProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /** 스타일 변형. icon: label 노출 없이 아이콘만 노출 (40×40), label: label 노출 있음 (기본). 사이즈 변형이 필요한 widget 은 className 으로 자체 책임. */
  variant?: ActionVariant;
  /** 아이콘 (내부 래핑) */
  icon: ReactElement;
  /** 식별자 — aria-label 자동생성, onAction 인자, data-action attribute */
  action: string;
  /** 아이콘 하단 텍스트 (variant="label"일 때만 표시) */
  label?: string;
  /**
   * navigation URL. 있으면 `<a href>` 로 렌더 (SEO 친화: 크롤러 link graph 포함),
   * 없으면 `<button>` 으로 렌더 (toggle / dialog open 등 non-navigation 의도).
   *
   * `<a>` 의 default navigation 은 React onClick 콜백 호출 후 자동으로 발생합니다.
   * SPA navigation 이 필요하면 onClick (또는 onAction) 핸들러에서 `e.preventDefault()`
   * 후 자체 router 를 호출하세요.
   *
   * 사용 권장: navigation 의도 action (search / my / cart / wishlist / 호스트 정의 page link)
   * 사용 비권장: toggle 의도 action (menu / category / back)
   */
  href?: string;
  /** `<a>` 모드 보조 — `target="_blank"` 등. button 모드에선 무시됨 */
  target?: string;
  /** `<a>` 모드 보조 — `rel="noopener noreferrer"` 등. button 모드에선 무시됨 */
  rel?: string;
  /** 스크롤 시에만 표시 (CSS class 토글, DOM 항상 유지) */
  showOnScroll?: boolean;
  /** 알림/뱃지 점(dot) 표시 */
  showDot?: boolean;
  /** 특정 디바이스에서만 노출. 미지정 = 항상 노출 */
  visibleOn?: VisibleOn;
  /** onAction 콜백에 전달할 메타데이터 */
  meta?: Record<string, unknown>;
  /**
   * 노출(impression) 트래킹 콜백. wrapper element 가 viewport 와 intersect 할 때마다 호출.
   *
   * 시그니처는 `onClick` / `onAction` 과 동일한 `(action, meta)` — 호스트가 single shared tracker
   * 로 여러 Action 의 impression 처리 가능 (호스트가 action 으로 분기 또는 meta 로 분류).
   *
   * **re-impression 패턴** — 한 번 노출 후 다시 비노출 → 재 노출 시 또 호출됨.
   * - 첫 viewport 진입: ✅ 호출
   * - viewport 이탈: ❌ 호출 안 함 (leave 콜백 아님)
   * - 재 진입 (스크롤로 돌아옴, `visibleOn`/`showOnScroll` 의 display:block 재전환 등): ✅ 호출
   *
   * `visibleOn` / `showOnScroll` 로 `display:none` 인 상태에서는 IntersectionObserver 가
   * entry 를 deliver 하지 않으므로 (브라우저 spec) 자동 비활성된다 — 호스트가 추가 가드 불필요.
   *
   * `threshold: 0` 고정. fine-grained 옵션이 필요하면 호스트가 자체 IntersectionObserver 를
   * ref 로 부착.
   *
   * `action` / `meta` 는 mount 시점의 값을 closure 로 capture — 동적 변경은 미지원
   * (impression 의 의미상 시작 시점의 식별자가 본질).
   */
  onImpressed?: (action: string, meta: Record<string, unknown>) => void;
  /**
   * anchor 박스 아래 떠 있는 풍선 형태 decoration (멤버십 배지 등).
   * - 위치/z-index/pointer-events 는 라이브러리가 통제 (디자인 시스템 표준).
   * - z-index 는 `--core-ui-header-action-bubble-z-index` CSS 변수로 override 가능.
   * - 스크롤 시(`isScrolled=true`) 자동 숨김 — 펼쳐진 헤더 상태에서만 노출.
   * - HeaderAction 자체가 `showOnScroll` 로 hidden 이거나 `visibleOn` 분기로 숨겨지면 bubble 도 같이 숨김.
   */
  bubble?: ReactElement;
  /**
   * bubble wrapper(`<span>`) 에 적용할 className.
   *
   * 라이브러리는 bubble 의 layout (width / max-width 등) 에 의견 없음 — 호스트가 직접 결정.
   *
   * **자주 쓰이는 케이스**:
   * - 자식이 `<img>` 등을 포함해 `max-width: 100%` reset 과 circular sizing 발생 시
   *   → `bubbleClassName="w-max"` (max-content) 로 wrapper width 명시
   * - viewport overflow 방지 → `bubbleClassName="w-max max-w-[240px]"`
   *
   * 예:
   * ```tsx
   * <Header.Action
   *   action="my"
   *   icon={<MyIcon />}
   *   bubble={<MembershipBadge type="gold_bubble" size="large" />}
   *   bubbleClassName="w-max"
   * />
   * ```
   */
  bubbleClassName?: string;
  /**
   * 현재 페이지 / 선택 상태. `true` 면 `activeIcon` 으로 교체 + `aria-current="page"` 부착 +
   * label active style 적용 (variant=label, desktop 한정).
   * wrapper span 에 `data-core-ui-action-active="true"` attribute 도 부착 (호스트 selector hook).
   *
   * `active=true` 시 `activeIcon` 도 *반드시* 명시 필요 — 미제공 시 dev warn.
   *
   * @default false
   */
  active?: boolean;
  /**
   * active 상태에서 표시할 icon. `active=true` 면 *필수* — NOL 디자인 시스템의 filled icon 변형
   * (예: `<IcMyFill />`) 명시 (BottomNavigation 과 동일 패턴).
   */
  activeIcon?: ReactElement;
  /**
   * wrapper span 에만 부착할 data-* attribute.
   *
   * 기본 `data-*` 는 interactive element(`<button>`/`<a>`) 에 전달된다.
   * wrapper selector hook 이 필요한 경우에만 별도로 사용한다.
   */
  wrapperDataAttrs?: WrapperDataAttributes;
}

const ACTION_LABELS: Record<string, string> = {
  menu: '메뉴',
  logo: '홈',
  search: '검색',
  my: '마이페이지',
  wishlist: '찜',
  cart: '장바구니',
  recent: '최근 본',
  category: '카테고리',
  back: '뒤로가기',
};

const VARIANT_CLASS = { icon: styles.variantIcon, label: undefined } as const;

export const HeaderAction = memo(
  forwardRef<HTMLButtonElement, HeaderActionProps>(
    (
      {
        icon,
        action,
        label,
        variant = 'label',
        href,
        target,
        rel,
        showOnScroll,
        showDot = false,
        meta = {},
        onClick,
        onImpressed,
        className,
        visibleOn,
        bubble,
        bubbleClassName,
        active = false,
        activeIcon,
        wrapperDataAttrs,
        'aria-label': ariaLabel,
        ...rest
      },
      ref,
    ) => {
      const { onAction } = useActionContext();
      const { isScrolled } = useScrollContext();

      const wrapperRef = useRef<HTMLSpanElement | null>(null);
      const onImpressedRef = useRef(onImpressed);
      onImpressedRef.current = onImpressed;

      useEffect(() => {
        if (!onImpressed) return undefined;
        if (typeof IntersectionObserver === 'undefined') return undefined;
        const el = wrapperRef.current;

        if (!el) return undefined;

        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) onImpressedRef.current?.(action, meta);
        });
        observer.observe(el);
        return () => {
          observer.disconnect();
        };
        // onImpressed 가 truthy 인 동안 mount 1회만 동작. 호스트가 mount 후 추가/제거하는 케이스는 미지원.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const isHidden = showOnScroll && !isScrolled;
      const resolvedAriaLabel = ariaLabel ?? (ACTION_LABELS[action] || action);
      const showLabel = variant === 'label' && label;
      // bubble 은 펼쳐진 헤더 상태에서만 노출 — 스크롤되면 자동 숨김.
      const showBubble = bubble && !isScrolled;
      // active 시 icon 교체 (BottomNavigation 패턴). activeIcon 미제공은 *정책 위반* — dev warn.
      const displayIcon = active && activeIcon ? activeIcon : icon;

      if (process.env.NODE_ENV !== 'production' && active && !activeIcon) {
        // eslint-disable-next-line no-console
        console.warn(
          `[core-ui] Header.Action active=true requires activeIcon prop (action="${action}"). ` +
            'Provide NOL filled icon variant (e.g., <IcMyFill />) for accurate visual state.',
        );
      }

      const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        onClick?.(e as React.MouseEvent<HTMLButtonElement>);

        if (!e.defaultPrevented) {
          onAction?.(action, meta, e as React.MouseEvent);
        }
      };

      const interactiveChildren = (
        <>
          <span className={cx(styles.iconWrapper, VARIANT_CLASS[variant])}>{displayIcon}</span>
          {showLabel ? (
            <span className={cx(styles.label, active && styles.labelActive)}>{label}</span>
          ) : null}
          {showDot ? <span aria-hidden className={styles.dot} /> : null}
        </>
      );

      // 항상 wrapper(span) 로 anchor(button) + (optional) bubble 을 묶음 — bubble 유무와 무관한 일관된 DOM 구조.
      // 호스트 className / visibleOn / hidden 은 항상 wrapper 에 부착 → 적용 위치가 일관, CSS 변수가 anchor + bubble 모두에 cascade.
      // ref / ...rest 는 interactive(anchor|button) 에 유지.
      // wrapper selector hook 이 필요한 경우에만 wrapperDataAttrs 로 명시적으로 전달.
      //
      // href 가 있으면 <a> 로 렌더 — 크롤러 link graph 인식 가능. SEO 친화.
      // href 가 없으면 기존처럼 <button> — toggle / dialog open 등 non-navigation 의도.
      //
      // `suppressHydrationWarning` 은 React 의 내부 prop — host inline script 가 hydration 전에 wrapper 의
      // attribute 를 mutate 하는 패턴 (LogoOrBack 등) 을 위해 wrapper 에 명시적으로 propagate.
      return (
        <span
          {...wrapperDataAttrs}
          className={cx(styles.actionContainer, isHidden && styles.hidden, className)}
          data-core-ui-action={action}
          data-core-ui-action-active={active || undefined}
          data-core-ui-hidden={isHidden || undefined}
          data-core-ui-visible-on={visibleOn}
          ref={wrapperRef}
          suppressHydrationWarning={rest.suppressHydrationWarning}
        >
          {href ? (
            <a
              {...pickDataAttrs(rest)}
              aria-current={active ? 'page' : undefined}
              aria-hidden={isHidden || undefined}
              aria-label={resolvedAriaLabel}
              className={cx(styles.action, VARIANT_CLASS[variant])}
              data-action={action}
              href={href}
              onClick={handleClick}
              ref={ref as unknown as React.Ref<HTMLAnchorElement>}
              rel={rel}
              tabIndex={isHidden ? -1 : undefined}
              target={target}
            >
              {interactiveChildren}
            </a>
          ) : (
            <button
              aria-current={active ? 'page' : undefined}
              aria-hidden={isHidden || undefined}
              aria-label={resolvedAriaLabel}
              className={cx(styles.action, VARIANT_CLASS[variant])}
              data-action={action}
              onClick={handleClick}
              ref={ref}
              tabIndex={isHidden ? -1 : undefined}
              type="button"
              {...rest}
            >
              {interactiveChildren}
            </button>
          )}
          {showBubble ? (
            <span aria-hidden className={cx(styles.bubble, bubbleClassName)}>
              {bubble}
            </span>
          ) : null}
        </span>
      );
    },
  ),
);

HeaderAction.displayName = 'Header.Action';
